import React from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import { Rotate as Hamburger } from 'hamburger-react';

import RichText from '@core/RichText';
import withLink from '@common/withLink';
import Seo from '@core/Seo';
import mq, { mqdown } from '@mq';
import { grid } from '@mixins';
import { fontM, fontXl } from '@fonts';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';

import EventForm from '@components/EventForm';
import Button from '@core/Button';

const EventsPage = ({
  data: {
    contentfulEventsPage: {
      title,
      text,
      imageDesktop,
      imageMobile,
      formTitle,
      pdfTitle,
      pdfEnglish,
      pdfGerman,
    },
  },
}) => (
  <>
    <Seo pageTitle={title} />
    <HamburgerContainer to="/">
      <Hamburger toggled />
    </HamburgerContainer>
    <Main>
      <Title>{title}</Title>
      <Text text={text} />
      <DesktopImage
        image={getImage(imageDesktop.gatsbyImageData)}
        alt={imageDesktop.description || imageDesktop.altText || imageDesktop.title || ''}
      />
      <MobileImage
        image={getImage(imageMobile.gatsbyImageData)}
        alt={imageMobile.description || imageMobile.altText || imageMobile.title || ''}
      />
      <Title id="events-form-title">{formTitle}</Title>
      <FormContainer>
        <EventForm />
      </FormContainer>
      <Title>{pdfTitle}</Title>
      <PdfContainer>
        <Button download to={`https:${pdfGerman.file.url}`}>
          {pdfGerman.title}
        </Button>
        <Button download to={`https:${pdfEnglish.file.url}`}>
          {pdfEnglish.title}
        </Button>
      </PdfContainer>
    </Main>
  </>
);

const Main = styled.main`
  ${grid};
  padding-top: 6rem;
  z-index: 1;
  position: relative;
  background-color: var(--color-background);
  padding-bottom: 10rem;
  /* justify-content: stretch; */

  ${mqdown.small} {
    margin-bottom: 6rem;
  }

  ${mq.small} {
    /* justify-items: center; */
    padding-bottom: 25rem;
  }
`;

const HamburgerContainer = withLink(styled.div`
  position: absolute;
  top: var(--gutter);
  right: var(--gutter);
  z-index: 3;
  transform: translate3d(0, 0, 0);
  color: var(--color-text);
`);

const Title = styled.h1`
  ${fontXl};
  grid-column: 1 / -1;
  margin-bottom: 1.5rem;

  ${mqdown.small} {
    font-size: 2.25rem;
  }

  ${mq.small} {
    grid-column: 3 / -3;
    text-align: center;
  }

  ${mq.medium} {
    grid-column: 4 / -4;
  }
`;

const Text = styled(RichText)`
  ${fontM};
  grid-column: 1 / -1;
  margin-bottom: 6rem;

  ${mq.small} {
    grid-column: 3 / -3;
    text-align: center;
  }

  ${mq.medium} {
    grid-column: 4 / -4;
  }
`;

const Image = styled(GatsbyImage)`
  grid-column: 1 / -1;
  margin-left: calc(-1 * var(--gutter));
  margin-right: calc(-1 * var(--gutter));
  margin-bottom: 10rem;
`;

const DesktopImage = styled(Image)`
  display: none;

  ${mq.small} {
    display: block;
  }
`;

const MobileImage = styled(Image)`
  ${mq.small} {
    display: none;
  }
`;

const FormContainer = styled.div`
  grid-column: 1 / -1;
  margin-bottom: 10rem;

  ${mqdown.small} {
    padding-left: 2rem;
    padding-right: 2rem;
  }

  ${mq.small} {
    grid-column: 4 / -4;
  }

  ${mq.medium} {
    grid-column: 5 / -5;
  }
`;

const PdfContainer = styled.div`
  grid-column: 1 / -1;
  margin-top: 1.5rem;
  display: grid;
  row-gap: 1.5rem;
  justify-content: center;

  ${mq.small} {
    grid-column: 3 / -3;
  }

  ${mq.medium} {
    grid-column: 4 / -4;
  }
`;

export const EventsPageQuery = graphql`
  query EventsPageQuery {
    contentfulEventsPage {
      title
      formTitle
      text {
        raw
      }
      imageDesktop {
        description
        title
        id
        gatsbyImageData(
          layout: FULL_WIDTH
          placeholder: DOMINANT_COLOR
          formats: [AUTO, AVIF, WEBP]
          quality: 75
        )
      }
      imageMobile {
        description
        title
        id
        gatsbyImageData(
          layout: FULL_WIDTH
          placeholder: DOMINANT_COLOR
          formats: [AUTO, AVIF, WEBP]
          quality: 75
        )
      }
      pdfTitle
      pdfGerman {
        title
        file {
          url
        }
      }
      pdfEnglish {
        title
        file {
          url
        }
      }
    }
  }
`;

export default EventsPage;
